import { useCallback, useRef, useEffect } from "react";
import { supabase } from "../../../supabaseClient";

export const useScoreManagement = (tournamentId, data, setData) => {
  // Debounce timer ref to prevent multiple rapid calls
  const debounceTimerRef = useRef({});
  // Track which scores are currently being saved
  const savingScoresRef = useRef(new Set());
  // Track pending local updates that haven't been saved to DB yet
  const pendingUpdatesRef = useRef(new Map());

  const updateScore = useCallback(
    async (teamId, bowlerId, gameNumber, score) => {
      try {
        // Input validation
        if (
          !teamId ||
          !bowlerId ||
          gameNumber === undefined ||
          gameNumber === null
        ) {
          throw new Error("Missing required parameters for score update");
        }

        if (score !== null && (isNaN(score) || score < 0 || score > 300)) {
          throw new Error(
            "Invalid score value. Score must be between 0 and 300"
          );
        }

        if (!data?.tournament) {
          throw new Error("Tournament data not available");
        }

        // IMMEDIATELY update local state for instant UI feedback
        setData((prevData) => {
          if (!prevData?.teams) return prevData;

          return {
            ...prevData,
            teams: prevData.teams.map((team) => {
              if (team.id === teamId) {
                return {
                  ...team,
                  transformedScores: {
                    ...team.transformedScores,
                    [bowlerId]:
                      team.transformedScores?.[bowlerId]?.map(
                        (scoreObj, index) => {
                          if (index === gameNumber) {
                            return {
                              ...scoreObj,
                              score: score,
                            };
                          }
                          return scoreObj;
                        }
                      ) || [],
                  },
                };
              }
              return team;
            }),
          };
        });

        // Create a unique key for this specific score update
        const debounceKey = `${teamId}-${bowlerId}-${gameNumber}`;

        // Clear any existing timer for this score
        if (debounceTimerRef.current[debounceKey]) {
          clearTimeout(debounceTimerRef.current[debounceKey]);
        }

        // Return a promise that resolves after the debounce delay
        return new Promise((resolve, reject) => {
          debounceTimerRef.current[debounceKey] = setTimeout(async () => {
            try {
              // Mark this score as being saved
              savingScoresRef.current.add(debounceKey);

              const gameNumberOneBased = gameNumber + 1;
              const { data: existingScore, error: fetchError } = await supabase
                .from("scores")
                .select("id")
                .eq("teamID", teamId)
                .eq("bowlerID", bowlerId)
                .eq("gameNumber", gameNumberOneBased)
                .maybeSingle();

              if (fetchError) {
                console.error("Error checking existing score:", fetchError);
                reject(new Error("Failed to save score to database"));
                return;
              }

              let saveError;
              let savedRow;
              if (existingScore) {
                const { data, error } = await supabase
                  .from("scores")
                  .update({ score })
                  .eq("id", existingScore.id)
                  .select("id")
                  .maybeSingle();
                saveError = error;
                savedRow = data;
              } else {
                const { data, error } = await supabase
                  .from("scores")
                  .insert({
                    teamID: teamId,
                    bowlerID: bowlerId,
                    gameNumber: gameNumberOneBased,
                    score,
                  })
                  .select("id")
                  .maybeSingle();
                saveError = error;
                savedRow = data;
              }

              if (saveError || !savedRow) {
                console.error("Error saving score:", saveError);
                reject(new Error("Failed to save score to database"));
                return;
              }

              resolve(true); // Success
            } catch (error) {
              console.error("Error in debounced score update:", error);
              reject(error);
            } finally {
              // ALWAYS clean up the timer reference and remove from saving set
              delete debounceTimerRef.current[debounceKey];
              savingScoresRef.current.delete(debounceKey);
            }
          }, 300); // 300ms debounce delay
        });
      } catch (error) {
        console.error("Error in updateScore:", error);
        // If there's an error in the outer try block, we still need to clean up
        const debounceKey = `${teamId}-${bowlerId}-${gameNumber}`;
        if (debounceTimerRef.current[debounceKey]) {
          clearTimeout(debounceTimerRef.current[debounceKey]);
          delete debounceTimerRef.current[debounceKey];
        }
        savingScoresRef.current.delete(debounceKey);
        throw error;
      }
    },
    [tournamentId, data, setData]
  );

  // Helper function to check if a score is currently being saved
  const isScoreSaving = useCallback((teamId, bowlerId, gameNumber) => {
    const debounceKey = `${teamId}-${bowlerId}-${gameNumber}`;
    return savingScoresRef.current.has(debounceKey);
  }, []);

  // Cleanup function to clear all pending saves (useful for component unmount)
  const clearAllPendingSaves = useCallback(() => {
    savingScoresRef.current.clear();
    Object.keys(debounceTimerRef.current).forEach((key) => {
      clearTimeout(debounceTimerRef.current[key]);
    });
    debounceTimerRef.current = {};
  }, []);

  // Safety timeout to clear stuck loading states (10 seconds)
  useEffect(() => {
    const safetyTimeout = setTimeout(() => {
      if (savingScoresRef.current.size > 0) {
        clearAllPendingSaves();
      }
    }, 10000);

    return () => clearTimeout(safetyTimeout);
  }, [clearAllPendingSaves]);

  return {
    updateScore,
    isScoreSaving,
    clearAllPendingSaves,
  };
};
